import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useBrandManager } from "@/contexts/BrandProvider";
import { OSMenuMetadata, OSMenuMetadataInsert, OSMenuMetadataUpdate } from "@/types/osMenuMetadata";
import { COMPLETE_MENU_STRUCTURE, MenuNode } from "@/config/menuStructure";

export function useOSMenuMetadata() {
  const { currentBrandId } = useBrandManager();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all metadata for current brand
  const { data: metadata, isLoading } = useQuery({
    queryKey: ['os-menu-metadata', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];
      
      const { data, error } = await supabase
        .from('os_menu_metadata' as any)
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('display_order', { ascending: true, nullsFirst: false });

      if (error) throw error;
      return data as unknown as OSMenuMetadata[];
    },
    enabled: !!currentBrandId,
  });

  // Fetch metadata by route path - memoized to prevent infinite loops
  const fetchMetadataByPath = useCallback(async (routePath: string): Promise<OSMenuMetadata | null> => {
    if (!currentBrandId) return null;

    const { data, error } = await supabase
      .from('os_menu_metadata' as any)
      .select('*')
      .eq('brand_id', currentBrandId)
      .eq('route_path', routePath)
      .maybeSingle();

    if (error) throw error;
    return data as unknown as OSMenuMetadata | null;
  }, [currentBrandId]);

  // Create metadata mutation
  const createMetadataMutation = useMutation({
    mutationFn: async (data: OSMenuMetadataInsert) => {
      const { data: result, error } = await supabase
        .from('os_menu_metadata' as any)
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-menu-metadata', currentBrandId] });
      toast({
        title: "Success",
        description: "Route metadata created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create route metadata",
        variant: "destructive",
      });
    },
  });

  // Update metadata mutation
  const updateMetadataMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: OSMenuMetadataUpdate }) => {
      const { data: result, error } = await supabase
        .from('os_menu_metadata' as any)
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-menu-metadata', currentBrandId] });
      toast({
        title: "Success",
        description: "Route metadata updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update route metadata",
        variant: "destructive",
      });
    },
  });

  // Delete metadata mutation
  const deleteMetadataMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_menu_metadata' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-menu-metadata', currentBrandId] });
      toast({
        title: "Success",
        description: "Route metadata deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete route metadata",
        variant: "destructive",
      });
    },
  });

  // Bulk update visibility mutation
  const bulkUpdateVisibilityMutation = useMutation({
    mutationFn: async ({ ids, visible }: { ids: string[]; visible: boolean }) => {
      const { error } = await supabase
        .from('os_menu_metadata' as any)
        .update({ visible })
        .in('id', ids);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-menu-metadata', currentBrandId] });
      toast({
        title: "Success",
        description: "Visibility updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update visibility",
        variant: "destructive",
      });
    },
  });

  // Sync from menu structure
  const syncFromMenuStructure = useMutation({
    mutationFn: async () => {
      if (!currentBrandId) throw new Error("No brand selected");

      // Flatten the menu structure to get all routes
      const flattenRoutes = (nodes: MenuNode[], parentPath = ""): OSMenuMetadataInsert[] => {
        const routes: OSMenuMetadataInsert[] = [];
        
        nodes.forEach((node) => {
          if (node.path) {
            routes.push({
              brand_id: currentBrandId,
              route_id: node.id,
              route_path: node.path,
              route_label: node.label,
              description: node.description || "",
              business_context: node.businessContext || "",
              seo_title: node.label,
              seo_description: node.description || "",
              visible: true,
            });
          }
          
          if (node.children) {
            routes.push(...flattenRoutes(node.children, node.label));
          }
        });
        
        return routes;
      };

      const allRoutes = flattenRoutes(COMPLETE_MENU_STRUCTURE);

      // Deduplicate routes by route_path (keep last occurrence)
      const uniqueRoutes = Array.from(
        new Map(allRoutes.map(route => [route.route_path, route])).values()
      );

      // Upsert all routes
      const { error } = await supabase
        .from('os_menu_metadata' as any)
        .upsert(
          uniqueRoutes,
          { 
            onConflict: 'brand_id,route_path',
            ignoreDuplicates: false 
          }
        );

      if (error) throw error;
      return uniqueRoutes.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['os-menu-metadata', currentBrandId] });
      toast({
        title: "Success",
        description: `Synced ${count} routes from menu structure`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to sync from menu structure",
        variant: "destructive",
      });
    },
  });

  return {
    metadata: metadata || [],
    isLoading,
    fetchMetadataByPath,
    createMetadata: createMetadataMutation.mutate,
    isCreating: createMetadataMutation.isPending,
    updateMetadata: updateMetadataMutation.mutate,
    isUpdating: updateMetadataMutation.isPending,
    deleteMetadata: deleteMetadataMutation.mutate,
    isDeleting: deleteMetadataMutation.isPending,
    bulkUpdateVisibility: bulkUpdateVisibilityMutation.mutate,
    isBulkUpdating: bulkUpdateVisibilityMutation.isPending,
    syncFromMenuStructure: syncFromMenuStructure.mutate,
    isSyncing: syncFromMenuStructure.isPending,
  };
}
